import { DownOutlined } from '@ant-design/icons';
import { Checkbox, Tree } from 'antd';
import { any, compose, filter, flatten, includes, isEmpty, length, map, path, prop, propEq, propOr, uniq, without, isNotEmpty } from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { errorMessage, successMessage } from '../../utils/messageMutation';
import { useTreeContext } from './treeContext';
import { hasParent, updateKeys } from './treeHelper';
import TreeRow from './TreeRow';

const Trees = ({ ElementRow, setElements, setTreeProps, setRefetchWidgets, elements, userIsAdmin, isPublished = false }) => {
  const { trees, addTree, updateTree, getElement, args, expandedKeys, onExpand, autoExpandParent, setCheckedKeys, checkedKeys, removeTree, selectAllOutsideFolders, allOutsideFolders, updateSelectAllOutsideFolders } = useTreeContext();

  const [draggingNodeKey, setDraggingNodeKey] = useState(null);

  useEffect(() => {
    if (setTreeProps) setTreeProps({ addTree, expandedKeys });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fix warning ResizeObserver loop completed with undelivered notifications
  useEffect(() => {
    const observer = new ResizeObserver(() => {});
    observer.observe(document.body);
    return () => {
      observer.disconnect();
    };
  }, []);

  const getAllDescendantKeys = useCallback((node) => {
    const children = prop('children', node);
    return children
      ? flatten([
        map(prop('key'), children),
        ...map(getAllDescendantKeys, children)
      ])
      : [];
  }, []);

  const isDropDisabled = useCallback((node, draggingNodeKey) => {
    if (!draggingNodeKey) return false;

    const nodeKey = prop('key', node);

    if (
      propEq(draggingNodeKey, 'key', node) ||
      (Array.isArray(checkedKeys) && includes(nodeKey, checkedKeys)) ||
      path(['element', 'isFolder'], node) === false
    ) return true;

    const descendants = getAllDescendantKeys(node);
    if (includes(draggingNodeKey, descendants)) return true;

    return false;
  }, [checkedKeys, getAllDescendantKeys]);

  const handleDrop = useCallback(({ node, dragNode, dragNodesKeys }) => {
    const rawKeysToMove = isNotEmpty(checkedKeys) ? checkedKeys : dragNodesKeys;

    const forbiddenDrop = any((key) => isDropDisabled(node, key), rawKeysToMove);
    if (
      forbiddenDrop ||
      propEq(false, 'allowAppendChildren', node) ||
      propEq(false, 'allowDrag', dragNode)
    ) return;

    const keyEndElement = prop('key', node);
    const keysToMove = without([keyEndElement], rawKeysToMove);
    const elementEnd = getElement(node);
    const nodes = [];

    /**
     * If the key is included in the `keysToMove` array, the node is pushed
     * to the `nodes` array. If the node has children, the function is called recursively with the children
     * and a new key.
     */
    const loop = (data, key) => {
      for (let i = 0; i < data.length; i++) {
        const iKey = path([i, 'key'], data);
        const children = path([i, 'children'], data);
        if (includes(iKey, keysToMove)) {
          nodes.push(data[i]);
        }
        if (children) {
          const newKey = key ? `${key}-${i}` : `${i}`;
          loop(children, newKey);
        }
      }
    };
    map((tree) => loop(trees, prop('key', tree)))(trees);
    const dragNodeElements = compose(
      map(getElement),
      flatten,
      uniq
    )(nodes);

    const updatedKeys = updateKeys(expandedKeys, keysToMove, 'add');

    const selectedKeys = map(prop('id'), dragNodeElements);
    const rootDragNodeElements = filter(node => {
      const parentId = prop('parent', node);
      return !parentId || !includes(parentId, selectedKeys);
    })(dragNodeElements);

    const mutationPromises = map((dragNodeElement) => new Promise((resolve, reject) => {
      const isFolder = prop('isFolder', dragNodeElement);
      /**
       * adds a new node to the end of a specified parent element in
       * a tree structure. If the parent element is not specified, the node is added to the root of the tree.
       */
      const addNodeOnElementEnd = (parentId) => addTree(parentId, updatedKeys, true)({
        name: prop('name', dragNodeElement),
        element: {
          elmId: prop('elmId', dragNodeElement),
          type: prop('type', args)
        }
      });

      const newParentId = propOr(null, 'treeId', elementEnd);
      const dragNodeId = prop('treeId', dragNodeElement);
      const elementEndIsFolder = prop('isFolder', elementEnd);

      try {
        const shouldRemove = length(keyEndElement) < length(prop('key', dragNodeElement)) && !isFolder && !elementEndIsFolder;
        if (shouldRemove) {
          removeTree(dragNodeId, dragNodeElement, !isFolder, true);
        } else if (hasParent(dragNodeElement) || isFolder) {
          updateTree(dragNodeId, updatedKeys, true)({ parent: newParentId });
        } else if (elementEndIsFolder) {
          addNodeOnElementEnd(newParentId);
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    }))(rootDragNodeElements);

    Promise.all(mutationPromises)
      .then(() => successMessage('ensemble des modifications', 'réalisé', true, false, false))
      .catch((error) => {
        errorMessage();
        console.error(error);
      });

    setCheckedKeys([]);
  }, [checkedKeys, getElement, trees, expandedKeys, setCheckedKeys, isDropDisabled, addTree, args, removeTree, updateTree]);

  const markDisabledNodes = useCallback(
    (nodes) => map(node => {
      const children = prop('children', node);
      return {
        ...node,
        disabled: isDropDisabled(node, draggingNodeKey),
        children: children ? markDisabledNodes(children) : []
      };
    })(nodes),
    [draggingNodeKey, isDropDisabled]
  );

  const treeData = useMemo(
    () => markDisabledNodes(trees),
    [markDisabledNodes, trees]
  );

  const isWidgetTree = propEq('WIDGET', 'type', args);
  const mayBeEdited = userIsAdmin && !isPublished;

  if (isEmpty(elements)) return <span>Aucune donnée.</span>;
  return (
    <>
      {isWidgetTree && mayBeEdited && (
        <Checkbox
          onChange={(e) => selectAllOutsideFolders(e.target.checked)}
          checked={allOutsideFolders}
          indeterminate={updateSelectAllOutsideFolders()}
          className="checkbox-all-outside"
        />
      )}
      <Tree
        treeData={treeData}
        showLine={{ showLeafIcon: false }}
        selectable={false}
        showIcon
        onDrop={handleDrop}
        draggable={mayBeEdited}
        checkable={mayBeEdited}
        switcherIcon={<DownOutlined />}
        titleRender={(data, idx) => (
          <TreeRow
            key={idx} data={data} setElements={setElements}
            ElementRow={ElementRow} setRefetchWidgets={setRefetchWidgets}
          />
        )}
        onDragStart={info => setDraggingNodeKey(path(['node', 'key'], info))}
        onDragEnd={() => setDraggingNodeKey(null)}
        onExpand={onExpand}
        expandedKeys={expandedKeys}
        autoExpandParent={autoExpandParent}
        onCheck={(keys) => {
          setCheckedKeys(keys);
          updateSelectAllOutsideFolders();
        }}
        checkedKeys={checkedKeys}
      />
    </>
  );
};

export default Trees;
